//Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
import { useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
// Constants
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { TraceLevel } from "@ms/uno-constants/lib/local/configuration/ISettings";
import { TaskEditorPanelDialogFallbackBreakpoint } from "@ms/uno-constants/lib/local/UIConstants";
// Controls
import { Dialog, DialogBody, DialogSurface, mergeClasses } from "@fluentui/react-components";
import { SuspenseSpinner } from "@ms/planner-generic-controls/lib/local/suspenseSpinner/SuspenseSpinner";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
// Containers
import { LazyAddNonMemberWarningDialogContainer } from "@ms/planner-container-controls/lib/local/containers/addNonMemberWarningDialogContainer/LazyAddNonMemberWarningDialogContainer";
import { LazyAppInfoModal } from "@ms/planner-container-controls/lib/local/containers/appInfoModal/LazyAppInfoModal";
import { LazyAuthErrorModal } from "@ms/planner-container-controls/lib/local/containers/authErrorModal/LazyAuthErrorModal";
import { LazyClearRecurrenceFromDueDateRemovalDialogContainer } from "@ms/planner-container-controls/lib/local/containers/clearRecurrenceFromDueDateRemovalDialog/LazyClearRecurrenceFromDueDateRemovalDialogContainer";
import { LazyConditionalColoringDialogContainer } from "@ms/planner-container-controls/lib/local/containers/conditionalColoringDialog/LazyConditionalColoringDialogContainer";
import { LazyCopyAndMoveTaskDialogContainer } from "@ms/planner-container-controls/lib/local/containers/copyAndMoveTask/LazyCopyAndMoveTaskDialogContainer";
import { LazyCopyPlanModalContainer } from "@ms/planner-container-controls/lib/local/containers/copyPlan/LazyCopyPlanModalContainer";
import { LazyCustomRecurrenceDialogContainer } from "@ms/planner-container-controls/lib/local/containers/customRecurrenceDialogContainer/LazyCustomRecurrenceDialogContainer";
import { LazyDeleteBucketDialogContainer } from "@ms/planner-container-controls/lib/local/containers/deleteBucketDialog/LazyDeleteBucketDialogContainer";
import { LazyDeleteCustomFieldDefinitionDialogContainer } from "@ms/planner-container-controls/lib/local/containers/deleteCustomFieldDefinitionDialog/LazyDeleteCustomFieldDefinitionDialogContainer";
import { LazyDeleteGoalDialogContainer } from "@ms/planner-container-controls/lib/local/containers/deleteGoalDialog/LazyDeleteGoalDialogContainer";
import { LazyDeletePlanDialogContainer } from "@ms/planner-container-controls/lib/local/containers/deletePlanDialog/LazyDeletePlanDialogContainer";
import { LazyDeleteRecurringTaskDialogContainer } from "@ms/planner-container-controls/lib/local/containers/deleteRecurringTaskDialog/LazyDeleteRecurringTaskDialogContainer";
import { LazyGoalsTaskSelectionPanelContainer } from "@ms/planner-container-controls/lib/local/containers/goalsTaskSelectionPanelContainer/LazyGoalsTaskSelectionPanelContainer";
import { LazyGroundingResourcesDialogContainer } from "@ms/planner-container-controls/lib/local/containers/groundingResourcesDialog/LazyGroundingResourcesDialogContainer";
import { LazyInfoModal } from "@ms/planner-container-controls/lib/local/containers/infoModal/LazyInfoModal";
import { LazyConnectToExistingPlanModalContainer } from "@ms/planner-container-controls/lib/local/containers/connectToExistingPlan/LazyConnectToExistingPlanModalContainer";
import { LazyNotificationSettingsDialogContainer } from "@ms/planner-container-controls/lib/local/containers/notification/LazyNotificationSettingsDialogContainer";
import { LazyPasteFailedDetailsDialogContainer } from "@ms/planner-container-controls/lib/local/containers/pasteFailedDetailsDialog/LazyPasteFailedDetailsDialogContainer";
import { LazyPublishPlanAsTemplateModalContainer } from "@ms/planner-container-controls/lib/local/containers/publishPlanAsTemplate/LazyPublishPlanAsTemplateModalContainer";
import { LazyRemoveMemberDialogContainer } from "@ms/planner-container-controls/lib/local/containers/removeMemberDialogContainer/LazyRemoveMemberDialogContainer";
import { LazyRenamePlanDialogContainer } from "@ms/planner-container-controls/lib/local/containers/renamePlanDialog/LazyRenamePlanDialogContainer";
import { LazySaveAndEditDynamicFilterModalContainer } from "@ms/planner-container-controls/lib/local/containers/saveAndEditDynamicFilter/LazySaveAndEditDynamicFilterModalContainer";
import { LazyDeleteStatusReportDialogContainer } from "@ms/planner-container-controls/lib/local/containers/reports/LazyDeleteStatusReportDialogContainer";
import { LazyRequestPremiumLicenseContainer } from "@ms/planner-container-controls/lib/local/containers/requestPremiumLicenseContainer/RequestPremiumLicenseContainerLazyLoader";
import { LazySharePlanDialogContainer } from "@ms/planner-container-controls/lib/local/containers/sharePlanDialogContainer/LazySharePlanDialogContainer";
import { LazyTeamsSelectPlanModalContainer } from "@ms/planner-container-controls/lib/local/containers/teamsSelectPlanModal/LazyTeamsSelectPlanModalContainer";
import { LazyTryPremiumContainer } from "@ms/planner-container-controls/lib/local/containers/tryPremiumContainer/TryPremiumContainerLazyLoader";
import { LazyUpsellStatusContainer } from "@ms/planner-container-controls/lib/local/containers/upsellStatusContainer/UpsellStatusContainerLazyLoader";
import { LazyGroundingRemoveResourceContainer } from "@ms/planner-container-controls/lib/local/controls/groundingResource/LazyGroundingRemoveResourceContainer";
import { LazyDeletePortfolioModalContainer } from "@ms/planner-container-controls/lib/local/controls/portfolio/LazyDeletePortfolioModalContainer";
import { LazyRenamePortfolioDialogContainer } from "@ms/planner-container-controls/lib/local/controls/portfolio/LazyRenamePortfolioDialogContainer";
import { LazyTaskAttachmentDetailsEditorContainer } from "@ms/planner-container-controls/lib/local/controls/taskEditor/LazyTaskAttachmentDetailsEditorContainer";
import { LazyTaskEditorContainer } from "@ms/planner-container-controls/lib/local/controls/taskEditor/LazyTaskEditorContainer";
import { LazyPlannerCreatePlanModalContainer } from "@ms/planner-create-plan/lib/local/containers/LazyPlannerCreatePlanModalContainer";
import { LazyTeamsTabCreatePlanModalContainer } from "@ms/planner-create-plan/lib/local/containers/LazyTeamsTabCreatePlanModalContainer";
import { LazyCreatePortfolioModalContainer } from "@ms/planner-create-plan/lib/local/containers/portfolio/LazyCreatePortfolioModalContainer";
import { LoopResponseSectionContainer } from "@ms/planner-loop/lib/local/controls/smart/loopResponseSection/LoopResponseSectionContainer";
import { SensitivityLabelWarningDialog } from "@ms/planner-presentational-controls/lib/local/groundingResourcesDialog/SensitivityLabelWarningDialog";
import { CopyMoveCustomizerMode } from "@ms/planner-presentational-controls/lib/local/taskCopyAndMoveCustomizer/TaskCopyAndMoveCustomizer.props";
// Hooks
import { useConfigProvider } from "@ms/planner-container-controls/lib/local/hooks/useConfigProvider";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { TaskEditorRenderMode } from "@ms/planner-models/lib/local/types/TaskEditorRenderMode";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Styles
import { useCreatePlanDialogStyles } from "@ms/planner-create-plan/lib/local/controls/presentational/createPlanDialog/CreatePlanDialog.styles";
import { useCreatePortfolioDialogStyles } from "@ms/planner-create-plan/lib/local/controls/presentational/portfolio/createPortfolioDialog/CreatePortfolioDialog.styles";
import { useConfirmationDialogStyles } from "@ms/planner-generic-controls/lib/local/confirmationDialog/ConfirmationDialog.styles";
import { useLinkDetailsEditorDialogStyles } from "@ms/planner-generic-controls/lib/local/linkDetailsEditor/LinkDetailsEditorDialog.styles";
import { useCopyAndMoveTaskDialogStyles } from "@ms/planner-presentational-controls/lib/local/copyAndMoveTask/CopyAndMoveTaskDialog.styles";
import { useCopyPlanModalStyles } from "@ms/planner-presentational-controls/lib/local/copyPlan/CopyPlanModal.styles";
import { useGroundingRemoveResourceDialogStyles } from "@ms/planner-presentational-controls/lib/local/groundingResourcesDialog/GroundingRemoveResourceDialog.styles";
import { useGroundingResourcesDialogStyles } from "@ms/planner-presentational-controls/lib/local/groundingResourcesDialog/GroundingResourcesDialog.styles";
import { useLinkToExistingPlanDialogStyles } from "@ms/planner-presentational-controls/lib/local/linkToExistingPlan/LinkToExistingPlanDialog.styles";
import { usePublishPlanAsTemplateModalStyles } from "@ms/planner-presentational-controls/lib/local/publishPlanAsTemplate/PublishPlanAsTemplateModal.styles";
import { useSelectPlanModalStyles } from "@ms/planner-presentational-controls/lib/local/selectPlanModal/SelectPlanModal.styles";
import { useSharePlanDialogStyles } from "@ms/planner-presentational-controls/lib/local/sharePlanDialog/SharePlanDialog.styles";
import { useTaskEditorContentStyles } from "@ms/planner-presentational-controls/lib/local/taskEditorContent/TaskEditorContent.styles";
import { useBaseTryPremiumModalStyles } from "@ms/planner-presentational-controls/lib/local/tryPremiumModal/TryPremiumModal.styles";
import { useUpsellStatusModalStyles } from "@ms/planner-presentational-controls/lib/local/upsellStatusModal/UpsellStatusModal.styles";
import { usePlannerAppDialogStyles } from "@ms/planner-presentational-controls/lib/local/utilities/BaseDialog.styles";
import { usePlannerModalLayersStyles } from "./PlannerModalLayers.styles";
// Utilities
import { format } from "@fluentui/utilities";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import React, { Suspense } from "react";
/**
 * Renders the modal layers for the Planner shell
 */ export const PlannerModalLayers = ({ authModule })=>{
    const [modalStore, viewStore] = useStores([
        Store.Modal,
        Store.View
    ]);
    const [modalAc] = useActionCreators([
        ActionCreator.Modal
    ]);
    const configProvider = useConfigProvider();
    const loggers = useLoggers();
    const defaultStyles = usePlannerModalLayersStyles();
    const baseDialogStyles = usePlannerAppDialogStyles();
    const taskEditorContentStyles = useTaskEditorContentStyles();
    const linkDetailsEditorDialogStyles = useLinkDetailsEditorDialogStyles();
    const sharePlanDialogStyles = useSharePlanDialogStyles();
    const createPlanDialogStyles = useCreatePlanDialogStyles();
    const createPortfolioDialogStyles = useCreatePortfolioDialogStyles();
    const selectPlanModalStyles = useSelectPlanModalStyles();
    const copyPlanModalStyles = useCopyPlanModalStyles();
    const copyAndMoveTaskDialogStyles = useCopyAndMoveTaskDialogStyles();
    const groundingResourcesDialogStyles = useGroundingResourcesDialogStyles();
    const groundingRemoveResourceDialogStyles = useGroundingRemoveResourceDialogStyles();
    const upsellStatusModalStyles = useUpsellStatusModalStyles();
    const tryPremiumModalStyles = useBaseTryPremiumModalStyles();
    const publishPlanAsTemplateModalStyles = usePublishPlanAsTemplateModalStyles();
    const linkToExistingPlanDialogStyles = useLinkToExistingPlanDialogStyles();
    const confirmationDialogStyles = useConfirmationDialogStyles();
    // Memoize fetchAccessTokenCallback to maintain stable reference
    const fetchAccessTokenCallback = React.useMemo(()=>async (resource, claims)=>{
            const tokenWithExpiry = await authModule.fetchAccessTokenAsync(resource, claims);
            return tokenWithExpiry.accessToken;
        }, [
        authModule
    ]);
    /**
     * Renders the Loop response section for task editor
     */ const renderLoopPage = (taskId)=>{
        return /*#__PURE__*/ React.createElement(LoopResponseSectionContainer, {
            fetchAccessTokenCallback: fetchAccessTokenCallback,
            taskId: taskId
        });
    };
    /**
     * Renders the modal layers
     */ const renderModalLayers = ()=>{
        const currentModalLayers = modalStore ? modalStore.getCurrentModalDataLayers() : [
            {
                modalType: ModalType.None
            }
        ];
        return /*#__PURE__*/ React.createElement(React.Fragment, null, currentModalLayers.map((currentModal, index)=>{
            // TaskEditor manages its own stable Dialog + Suspense to avoid double-Dialog flicker during lazy loading
            if (currentModal.modalType === ModalType.TaskEditor) {
                return /*#__PURE__*/ React.createElement(React.Fragment, {
                    key: index
                }, renderModal(currentModal));
            }
            return /*#__PURE__*/ React.createElement(Suspense, {
                key: index,
                fallback: renderEmptyModal(currentModal.modalType)
            }, renderModal(currentModal));
        }));
    };
    /**
     * Gets the dialog surface dimensions for the appropriate modal type
     */ const getEmptyDialogSurfaceStyles = (modalType)=>{
        switch(modalType){
            case ModalType.TaskEditor:
                return taskEditorContentStyles.surface;
            case ModalType.TaskAttachmentDetailsEditor:
                return linkDetailsEditorDialogStyles.dialogSurface;
            case ModalType.SharePlan:
                return sharePlanDialogStyles.surface;
            case ModalType.CreatePlan:
                return createPlanDialogStyles.root;
            case ModalType.CreatePortfolio:
                return createPortfolioDialogStyles.root;
            case ModalType.SelectPlan:
                return selectPlanModalStyles.root;
            case ModalType.CopyPlan:
                return copyPlanModalStyles.root;
            case ModalType.MoveTask:
            case ModalType.CopyTask:
                return copyAndMoveTaskDialogStyles.dialogSurface;
            case ModalType.GroundingResources:
                return groundingResourcesDialogStyles.dialogSurface;
            case ModalType.DeleteGroundingResource:
                return groundingRemoveResourceDialogStyles.root;
            case ModalType.UpsellStatus:
                return upsellStatusModalStyles.dialogSurface;
            case ModalType.TryPremium:
                return tryPremiumModalStyles.dialogSurface;
            case ModalType.PublishPlanAsTemplate:
                return publishPlanAsTemplateModalStyles.root;
            case ModalType.LinkToExistingPlan:
                return linkToExistingPlanDialogStyles.dialogSurface;
            // Remaining dialogs use confirmationDialog styles
            case ModalType.DeleteBucket:
            case ModalType.DeleteCustomFieldDefinition:
            case ModalType.DeletePlan:
            case ModalType.DeletePortfolio:
            case ModalType.DeleteGoal:
            case ModalType.DeleteRecurringTask:
            case ModalType.DeleteStatusReport:
            case ModalType.RemoveMember:
            case ModalType.ClearRecurrenceFromDueDateRemoval:
            case ModalType.ConvertPlan:
            case ModalType.CopilotSidecar:
            case ModalType.RenameDialog:
            case ModalType.RenamePortfolioDialog:
            case ModalType.RequestPremiumLicense:
            case ModalType.None:
            case ModalType.Info:
            case ModalType.AppInfo:
            case ModalType.AddNonMemberWarning:
            case ModalType.NotificationSettingsDialog:
            case ModalType.GoalsTaskSelectionPanel:
            case ModalType.CustomRecurrence:
            case ModalType.FormsIFrame:
            case ModalType.AuthError:
            case ModalType.FRE:
            case ModalType.MaximumAgentsLimitReached:
            case ModalType.DeleteGroup:
            case ModalType.ShareFileWarning:
            case ModalType.AssignTaskConfirmation:
            case ModalType.ICalendarDialog:
            case ModalType.LeavePlan:
            case ModalType.AssigneeLimitError:
            case ModalType.UpsellModal:
            case ModalType.GroundingSensitivityWarning:
            case ModalType.ConditionalColoring:
            case ModalType.SaveDynamicFilter:
            case ModalType.EditDynamicFilter:
            case ModalType.PasteFailedDetails:
                return confirmationDialogStyles.dialogSurface;
            default:
                ErrorUtilities.unreachableCase(modalType, (log)=>{
                    loggers?.traceLogger.logTrace(0x1e04f6c7 /* tag_4bp1h */ , TraceLevel.Error, log);
                });
                return confirmationDialogStyles.dialogSurface;
        }
    };
    /**
     * Renders the empty modal for spinner
     */ const renderEmptyModal = (modalType)=>{
        return /*#__PURE__*/ React.createElement(Dialog, {
            open: true,
            modalType: "modal"
        }, /*#__PURE__*/ React.createElement(DialogSurface, {
            className: mergeClasses(getEmptyDialogSurfaceStyles(modalType), defaultStyles.emptyModal)
        }, /*#__PURE__*/ React.createElement(DialogBody, {
            className: defaultStyles.emptyModal
        }, /*#__PURE__*/ React.createElement(SuspenseSpinner, null))));
    };
    /**
     * Renders the task editor modal with a stable Dialog wrapper and Suspense inside,
     * so the Dialog never unmounts during lazy loading (prevents flicker).
     * Returns null if panel mode is active (rendered by PlannerShell instead).
     */ const renderTaskEditorModal = (taskId, initialTab, onDismiss)=>{
        const isTaskEditorPanelEnabled = configProvider().flights.EnableTaskEditorPanel;
        const taskEditorRenderMode = viewStore.getTaskEditorRenderMode();
        const isNarrowViewport = window.innerWidth <= TaskEditorPanelDialogFallbackBreakpoint;
        if (isTaskEditorPanelEnabled && taskEditorRenderMode === TaskEditorRenderMode.Panel && !isNarrowViewport) {
            return null;
        }
        // Render a single stable Dialog with Suspense INSIDE the DialogBody.
        // The container renders TaskEditorContent (content only, no Dialog wrapper),
        // preventing the double-Dialog swap that caused visible flicker during lazy loading.
        // aria-label provides a fallback label during Suspense loading; once DialogTitle
        // mounts inside TaskEditorContent, aria-labelledby auto-overrides per ARIA spec.
        return /*#__PURE__*/ React.createElement(Dialog, {
            open: true,
            modalType: "modal",
            onOpenChange: (_, data)=>!data.open && (modalAc.closeModal(), onDismiss?.())
        }, /*#__PURE__*/ React.createElement(DialogSurface, {
            "aria-label": format(Strings.TaskEditorDialog_DialogAriaLabel, ""),
            className: mergeClasses(baseDialogStyles.surface, taskEditorContentStyles.surface),
            "data-commit-on-resize": true
        }, /*#__PURE__*/ React.createElement(DialogBody, {
            className: taskEditorContentStyles.body
        }, /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement("div", {
                className: defaultStyles.emptyModal
            }, /*#__PURE__*/ React.createElement(SuspenseSpinner, null))
        }, /*#__PURE__*/ React.createElement(LazyTaskEditorContainer, {
            taskId: taskId,
            renderLoopPage: ()=>renderLoopPage(taskId),
            initialTab: initialTab,
            renderMode: TaskEditorRenderMode.Dialog,
            showToggleRenderModeButton: isTaskEditorPanelEnabled && !isNarrowViewport,
            onDismiss: onDismiss
        })))));
    };
    /**
     * Renders the modal based on modal type
     */ const renderModal = (currentModal)=>{
        switch(currentModal.modalType){
            case ModalType.TaskEditor:
                return renderTaskEditorModal(currentModal.taskId, currentModal.initialTab, currentModal.onDismiss);
            case ModalType.CreatePlan:
                return "teamsGroupId" in currentModal ? /*#__PURE__*/ React.createElement(LazyTeamsTabCreatePlanModalContainer, {
                    selectedGroupId: currentModal.teamsGroupId,
                    tabId: currentModal.teamsTabId,
                    channelId: currentModal.teamsChannelId,
                    channelType: currentModal.channelType,
                    onDismiss: modalAc.closeModal
                }) : /*#__PURE__*/ React.createElement(LazyPlannerCreatePlanModalContainer, {
                    onDismiss: modalAc.closeModal
                });
            case ModalType.CustomRecurrence:
                return /*#__PURE__*/ React.createElement(LazyCustomRecurrenceDialogContainer, {
                    taskId: currentModal.taskId
                });
            case ModalType.ClearRecurrenceFromDueDateRemoval:
                return /*#__PURE__*/ React.createElement(LazyClearRecurrenceFromDueDateRemovalDialogContainer, {
                    taskId: currentModal.taskId,
                    onDismiss: modalAc.closeModal
                });
            case ModalType.RenamePortfolioDialog:
                return /*#__PURE__*/ React.createElement(LazyRenamePortfolioDialogContainer, null);
            case ModalType.RenameDialog:
                return /*#__PURE__*/ React.createElement(LazyRenamePlanDialogContainer, null);
            case ModalType.DeletePortfolio:
                return /*#__PURE__*/ React.createElement(LazyDeletePortfolioModalContainer, null);
            case ModalType.SharePlan:
                return /*#__PURE__*/ React.createElement(LazySharePlanDialogContainer, {
                    planId: currentModal.planId,
                    initialSelectedMembersIds: currentModal.userIds,
                    initialMode: currentModal.initialMode,
                    onSuccess: currentModal.onSuccess
                });
            case ModalType.DeleteBucket:
                return /*#__PURE__*/ React.createElement(LazyDeleteBucketDialogContainer, {
                    bucketId: currentModal.bucketId,
                    onDismiss: currentModal.onDismiss
                });
            case ModalType.DeleteRecurringTask:
                return /*#__PURE__*/ React.createElement(LazyDeleteRecurringTaskDialogContainer, {
                    taskId: currentModal.taskId,
                    onDeleteDismiss: currentModal.onDismiss,
                    onDismiss: modalAc.closeModal
                });
            case ModalType.DeleteCustomFieldDefinition:
                return /*#__PURE__*/ React.createElement(LazyDeleteCustomFieldDefinitionDialogContainer, {
                    customFieldDefinitionId: currentModal.customFieldDefinitionId,
                    onDismiss: currentModal.onDismiss
                });
            case ModalType.DeleteGoal:
                return /*#__PURE__*/ React.createElement(LazyDeleteGoalDialogContainer, {
                    goalId: currentModal.goalId,
                    onDismiss: modalAc.closeModal
                });
            case ModalType.GoalsTaskSelectionPanel:
                return /*#__PURE__*/ React.createElement(LazyGoalsTaskSelectionPanelContainer, {
                    goalId: currentModal.goalId,
                    planId: currentModal.planId,
                    onDismiss: modalAc.closeModal
                });
            case ModalType.AppInfo:
                return /*#__PURE__*/ React.createElement(LazyAppInfoModal, {
                    onDismiss: modalAc.closeModal
                });
            case ModalType.DeletePlan:
                return /*#__PURE__*/ React.createElement(LazyDeletePlanDialogContainer, {
                    planId: currentModal.planId
                });
            case ModalType.DeleteStatusReport:
                return /*#__PURE__*/ React.createElement(LazyDeleteStatusReportDialogContainer, {
                    modalData: currentModal
                });
            case ModalType.TaskAttachmentDetailsEditor:
                return /*#__PURE__*/ React.createElement(LazyTaskAttachmentDetailsEditorContainer, {
                    taskId: currentModal.taskId,
                    contentLink: currentModal.contentLink
                });
            case ModalType.DeleteGroundingResource:
                return /*#__PURE__*/ React.createElement(LazyGroundingRemoveResourceContainer, {
                    planId: currentModal.planId,
                    resourceItem: currentModal.resourceItem,
                    sourceComponentName: currentModal.sourceComponentName
                });
            case ModalType.SelectPlan:
                return /*#__PURE__*/ React.createElement(LazyTeamsSelectPlanModalContainer, {
                    onDismiss: async ()=>modalAc.closeModal()
                });
            case ModalType.UpsellStatus:
                return /*#__PURE__*/ React.createElement(LazyUpsellStatusContainer, null);
            case ModalType.AddNonMemberWarning:
                return /*#__PURE__*/ React.createElement(LazyAddNonMemberWarningDialogContainer, {
                    userId: currentModal.userId,
                    planId: currentModal.planId,
                    onAddAndAssignTask: currentModal.onAddAndAssignTask
                });
            case ModalType.CopyPlan:
                return /*#__PURE__*/ React.createElement(LazyCopyPlanModalContainer, {
                    planId: currentModal.planId,
                    onDismiss: modalAc.closeModal
                });
            case ModalType.NotificationSettingsDialog:
                return /*#__PURE__*/ React.createElement(LazyNotificationSettingsDialogContainer, null);
            case ModalType.RequestPremiumLicense:
                return /*#__PURE__*/ React.createElement(LazyRequestPremiumLicenseContainer, null);
            case ModalType.RemoveMember:
                return /*#__PURE__*/ React.createElement(LazyRemoveMemberDialogContainer, {
                    planId: currentModal.planId,
                    userId: currentModal.userId
                });
            case ModalType.MoveTask:
                {
                    // Invoke caller-provided onDismiss after closeModal so callers can restore focus
                    // to a stable anchor (e.g. the canvas-grid root). Without this, focus falls to
                    // document.body after the dialog closes, causing Narrator to read a verbose
                    // window/region/landmark preamble before the success toast.
                    const callerOnDismiss = currentModal.onDismiss;
                    return /*#__PURE__*/ React.createElement(LazyCopyAndMoveTaskDialogContainer, {
                        taskIds: currentModal.taskIds,
                        mode: CopyMoveCustomizerMode.Move,
                        onDismiss: async ()=>{
                            await modalAc.closeModal();
                            callerOnDismiss();
                        }
                    });
                }
            case ModalType.CopyTask:
                {
                    const callerOnDismiss = currentModal.onDismiss;
                    return /*#__PURE__*/ React.createElement(LazyCopyAndMoveTaskDialogContainer, {
                        taskIds: [
                            currentModal.taskId
                        ],
                        mode: CopyMoveCustomizerMode.Copy,
                        onDismiss: async ()=>{
                            await modalAc.closeModal();
                            callerOnDismiss();
                        }
                    });
                }
            case ModalType.CreatePortfolio:
                return /*#__PURE__*/ React.createElement(LazyCreatePortfolioModalContainer, null);
            case ModalType.TryPremium:
                return /*#__PURE__*/ React.createElement(LazyTryPremiumContainer, null);
            case ModalType.GroundingResources:
                return /*#__PURE__*/ React.createElement(LazyGroundingResourcesDialogContainer, {
                    planId: currentModal.planId,
                    onClose: modalAc.closeModal,
                    sourceComponentName: currentModal.sourceComponentName
                });
            case ModalType.PublishPlanAsTemplate:
                return /*#__PURE__*/ React.createElement(LazyPublishPlanAsTemplateModalContainer, {
                    planId: currentModal.planId,
                    onDismiss: async ()=>modalAc.closeModal()
                });
            case ModalType.Info:
                return /*#__PURE__*/ React.createElement(LazyInfoModal, {
                    headerText: currentModal.headerText,
                    message: currentModal.message,
                    buttonText: currentModal.buttonText,
                    correlationId: currentModal.correlationId,
                    onButtonClick: currentModal.onButtonClick,
                    onDismiss: modalAc.closeModal
                });
            case ModalType.GroundingSensitivityWarning:
                return /*#__PURE__*/ React.createElement(SensitivityLabelWarningDialog, {
                    planSensitivityLabelName: currentModal.planSensitivityLabelName,
                    fileSensitivityLabelName: currentModal.fileSensitivityLabelName,
                    onAddFileAnyway: ()=>{
                        currentModal.onAddFileAnyway();
                        modalAc.closeModal();
                    },
                    onDismiss: modalAc.closeModal
                });
            case ModalType.ConditionalColoring:
                return /*#__PURE__*/ React.createElement(LazyConditionalColoringDialogContainer, {
                    planId: currentModal.planId,
                    onDismiss: modalAc.closeModal
                });
            case ModalType.SaveDynamicFilter:
            case ModalType.EditDynamicFilter:
                return /*#__PURE__*/ React.createElement(LazySaveAndEditDynamicFilterModalContainer, {
                    modalData: currentModal,
                    onDismiss: modalAc.closeModal
                });
            case ModalType.PasteFailedDetails:
                return /*#__PURE__*/ React.createElement(LazyPasteFailedDetailsDialogContainer, {
                    modalData: currentModal
                });
            case ModalType.AuthError:
                return /*#__PURE__*/ React.createElement(LazyAuthErrorModal, {
                    onSignInButtonClick: currentModal.onSignInButtonClick,
                    isPopupBlockedError: currentModal.isPopupBlockedError,
                    onDismiss: modalAc.closeModal
                });
            case ModalType.LinkToExistingPlan:
                {
                    const callerOnDismiss = currentModal.onDismiss;
                    return /*#__PURE__*/ React.createElement(LazyConnectToExistingPlanModalContainer, {
                        sourceTaskId: currentModal.sourceTaskId,
                        onDismiss: async ()=>{
                            await modalAc.closeModal();
                            callerOnDismiss?.();
                        }
                    });
                }
            // TODO: Add other modal types here
            case ModalType.ConvertPlan:
            case ModalType.CopilotSidecar:
            case ModalType.FormsIFrame:
            case ModalType.FRE:
            case ModalType.MaximumAgentsLimitReached:
            case ModalType.DeleteGroup:
            case ModalType.None:
            case ModalType.AssigneeLimitError:
            case ModalType.UpsellModal:
                return null;
            default:
                ErrorUtilities.unreachableCase(currentModal, (log)=>{
                    loggers?.traceLogger.logTrace(0x1e04f6c6 /* tag_4bp1g */ , TraceLevel.Error, log);
                });
                return null;
        }
    };
    return renderModalLayers();
};
